import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import PublicNavbar from "../components/layout/PublicNavbar";
import DebtNavbar from "../components/layout/DebtNavbar";
import { Button, Input } from "../components/common/Primitives";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiError } from "../lib/api";
import Seo from "../components/Seo";
import { Rocket, HandCoins, TrendingUp, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * Intent-aware login surface.
 *
 * `/login`                    -> AuthChooser (mode=login)
 * `/for-startups/login`       -> intent="equity"
 * `/money/login`         -> intent="debt"    (copper theme)
 * `/for-investors/login`      -> intent="investor"
 *
 * Post-auth redirects always honour the user's actual role first (a
 * platform admin still lands on `/admin/*`). The intent only re-routes
 * the "normal" personas — e.g. a startup who signed in via `/money/login`
 * lands on `/my-debt-cases` instead of the equity dashboard.
 */
export default function Login({ intent = "equity" }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const isDebt = intent === "debt";
  const isInvestor = intent === "investor";

  const go = async (user) => {
    const role = user?.role;

    // Admins always land on admin surfaces regardless of intent.
    if (role === "super_admin" || role === "admin" || role === "manager" || role === "agent") {
      nav("/admin/dashboard"); return;
    }
    if (["debt_head", "debt_manager", "debt_agent", "credit_analyst"].includes(role)) {
      nav("/admin/money/dashboard"); return;
    }
    if (role === "channel_partner") { nav("/partner/dashboard"); return; }

    // Debt-intent: startup / investor accounts both go into the debt flow.
    if (isDebt) {
      try {
        const r = await api.get("/debt/cases", { params: { limit: 1 } });
        const hasCases = (r.data?.items || []).length > 0;
        nav(hasCases ? "/my-debt-cases" : "/money?prefill=1");
      } catch {
        nav("/money?prefill=1");
      }
      return;
    }
    // Investor-intent
    if (isInvestor || role === "investor") { nav("/investor/dashboard"); return; }
    // Equity-intent default
    nav("/startup/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, rememberMe);
      toast.success("Welcome back.");
      await go(user);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    sessionStorage.setItem("venturaz_pending_remember_me", rememberMe ? "1" : "0");
    // Carry the intent through the OAuth round-trip so the callback
    // can honour our themed redirect on return.
    sessionStorage.setItem("venturaz_pending_login_intent", intent);
    const backend = process.env.REACT_APP_BACKEND_URL;
    const from = new URLSearchParams(loc.search).get("from") || "/";
    window.location.href = `${backend}/api/auth/google/init?redirect=${encodeURIComponent(from)}`;
  };

  const themeCfg = isDebt ? {
    heroClass: "bg-gradient-to-br from-[#FBFAF5] via-[#F5F1E8] to-[#E8DFC9]",
    overline: "VENTURAZ MONEY · SECURE LOGIN",
    heading: "Welcome back — pick up your loan case.",
    sub: "Compare offers from 85+ banks & NBFCs. Free CIBIL. Advisor, not lender.",
    Icon: HandCoins,
    accentText: "text-[#7A4E1D]",
    accentRing: "focus:ring-[#B8860B]",
    panelExtra: "border-t-4 border-[#B8860B]",
    otherCta: [
      { label: "I'm raising equity", to: "/for-startups/login" },
      { label: "I invest in startups", to: "/for-investors/login" },
    ],
  } : isInvestor ? {
    heroClass: "liquid-morph-hero",
    overline: "VENTURAZ · INVESTOR LOGIN",
    heading: "Welcome back — see today's curated deals.",
    sub: "Hand-picked opportunities across sectors. Access DD, deal rooms, and native e-sign.",
    Icon: TrendingUp,
    accentText: "text-gold",
    accentRing: "focus:ring-gold",
    panelExtra: "",
    otherCta: [
      { label: "I'm raising equity", to: "/for-startups/login" },
      { label: "I need a loan", to: "/money/login" },
    ],
  } : {
    heroClass: "liquid-morph-hero",
    overline: "VENTURAZ · FOUNDER LOGIN",
    heading: "Welcome back — pick up where you left off.",
    sub: "Continue your fundraise. Track matches, deal rooms, docs and diligence in one place.",
    Icon: Rocket,
    accentText: "text-gold",
    accentRing: "focus:ring-gold",
    panelExtra: "",
    otherCta: [
      { label: "I need a loan", to: "/money/login" },
      { label: "I invest in startups", to: "/for-investors/login" },
    ],
  };
  const { Icon } = themeCfg;

  const NavBar = isDebt ? DebtNavbar : PublicNavbar;

  return (
    <div className={`min-h-screen relative ${isDebt ? "text-ink debt-shell" : "text-cream"} ${themeCfg.heroClass} !border-0 !shadow-none !rounded-none`}>
      <Seo
        title={`Sign In (${intent}) | VenturaZ`}
        description="Sign in to your VenturaZ account to access your dashboard, matches, deal rooms and secure document vault."
        path={`/login/${intent}`}
        noindex
      />
      <Toaster richColors position="top-right" />
      <NavBar />
      <section className="relative max-w-md mx-auto px-6 py-14 lg:py-20">
        <Link
          to="/login"
          className={`inline-flex items-center gap-1 text-xs mb-4 hover:underline ${isDebt ? "text-[#7A4E1D]" : "text-cream/80 hover:text-gold"}`}
          data-testid="login-back-to-chooser"
        >
          <ArrowLeft size={12} /> Different surface? Change
        </Link>
        <div className={`overline mb-3 ${isDebt ? "text-[#7A4E1D]" : "text-gold/90"}`} data-testid="login-overline">
          {themeCfg.overline}
        </div>
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-10 h-10 rounded-lg inline-flex items-center justify-center ${isDebt ? "bg-[#7A4E1D] text-[#FFF3D9]" : "bg-gold text-navy"}`}>
            <Icon size={18} />
          </div>
          <div>
            <h1 className={`font-display text-2xl leading-tight ${isDebt ? "text-ink" : "text-cream"}`} data-testid="login-heading">
              {themeCfg.heading}
            </h1>
            <div className={`text-xs mt-0.5 ${isDebt ? "text-ink-muted" : "text-cream/70"}`}>{themeCfg.sub}</div>
          </div>
        </div>
        <form
          onSubmit={submit}
          className={`space-y-4 liquid-morph-panel rounded-2xl p-6 text-ink ${themeCfg.panelExtra}`}
          data-testid="login-form"
        >
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="login-email" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="login-password" />
          <label className="flex items-start gap-2 text-xs text-ink-soft cursor-pointer select-none" data-testid="login-remember-row">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className={`mt-0.5 accent-gold h-3.5 w-3.5 ${themeCfg.accentRing}`}
              data-testid="login-remember-checkbox"
            />
            <span className="leading-snug">
              <strong className="text-ink">Remember me on this device</strong>
              <span className="text-ink-soft"> — leave unchecked on shared laptops. Otherwise the session ends when you close the browser.</span>
            </span>
          </label>
          <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign In"}
          </Button>
          <div className="flex items-center gap-3 text-xs text-ink-soft">
            <div className="flex-1 h-px bg-navy/15" /> OR <div className="flex-1 h-px bg-navy/15" />
          </div>
          <button type="button" onClick={google} className="w-full py-2.5 border border-navy/20 text-ink-soft font-heading text-sm hover:border-[#8A6600] hover:text-[#8A6600] transition-colors" data-testid="login-google-btn">
            Continue with Google
          </button>
          <div className="text-sm text-ink-soft text-center pt-2">
            New here?{" "}
            <Link
              to={isDebt ? "/money/signup" : isInvestor ? "/for-investors/signup" : "/for-startups/signup"}
              className="text-[#8A6600] font-semibold hover:underline"
              data-testid="login-signup-link"
            >Create an account →</Link>
          </div>
          <div className="text-xs text-ink-soft text-center flex items-center justify-center gap-3">
            <Link to="/forgot-password" className="hover:text-[#8A6600]" data-testid="login-forgot-link">Forgot password?</Link>
            <span className="text-ink-soft">·</span>
            <Link to="/admin/login" className="hover:text-[#8A6600]">Admin login</Link>
          </div>
        </form>

        {/* Cross-surface hint — a debt user can jump to the equity login without another click on /login. */}
        <div className="mt-6 text-xs text-center">
          {themeCfg.otherCta.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className={`mx-2 inline-block hover:underline ${isDebt ? "text-[#7A4E1D]" : "text-cream/80 hover:text-gold"}`}
              data-testid={`login-crosslink-${c.to.replace(/\//g, "-").slice(1)}`}
            >
              {c.label} →
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
